import React, { useState, useRef, useEffect, useMemo } from 'react';
import './AnimatedNotes.css';
import lowGNote from '../assets/LowGNote.png';
import lowANote from '../assets/LowANote.png';
import bNote from '../assets/BNote.png';
import cNote from '../assets/CNote.png';
import dNote from '../assets/DNote.png';
import eNote from '../assets/ENote.png';
import fNote from '../assets/FNote.png';
import highGNote from '../assets/HighGNote.png';
import highANote from '../assets/HighANote.png';

const AnimatedNotes = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const scrollRef = useRef(null);
  const positionRef = useRef(0);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const lastPlayedPositionRef = useRef(null);

  // Wrap notes array in useMemo
  const notes = useMemo(() => {
    const scaleUp = [
      { src: lowGNote, name: 'Low G', frequency: 426 },
      { src: lowANote, name: 'Low A', frequency: 480 },
      { src: bNote, name: 'B', frequency: 540 },
      { src: cNote, name: 'C', frequency: 600 },
      { src: dNote, name: 'D', frequency: 640 },
      { src: eNote, name: 'E', frequency: 720 },
      { src: fNote, name: 'F', frequency: 800 },
      { src: highGNote, name: 'High G', frequency: 840 },
      { src: highANote, name: 'High A', frequency: 960 },
    ];
    const scaleDown = [...scaleUp].reverse();
    return [...scaleUp, ...scaleDown];
  }, []); // Empty dependency array since notes are constant

  const playTone = frequency => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);

    // Start the tone
    oscillator.start();

    // Stop the tone after 1 second
    oscillator.stop(audioContext.currentTime + 1);
  };

  useEffect(() => {
    const animate = () => {
      if (!scrollRef.current) return;

      positionRef.current -= 1; // Adjust speed here

      // Calculate the center position
      const containerWidth = 800;
      const centerX = containerWidth / 2;
      const noteWidth = 50;

      // Calculate which note is at the center line
      const totalNotes = notes.length;
      const currentPosition = -positionRef.current;
      const noteIndex = Math.floor((currentPosition + centerX) / noteWidth) % totalNotes;

      // Check if a note is crossing the center line
      const notePosition = (currentPosition + centerX) % noteWidth;

      // Play note when it crosses the center (with a small threshold to prevent multiple triggers)
      if (notePosition >= 0 && notePosition <= 2) {
        const currentNoteIndex = noteIndex % notes.length;

        // Prevent the same note from playing multiple times
        if (lastPlayedPositionRef.current !== currentNoteIndex) {
          playTone(notes[currentNoteIndex].frequency);
          lastPlayedPositionRef.current = currentNoteIndex;
        }
      }

      // Reset position when all notes have scrolled
      if (positionRef.current <= -900) {
        positionRef.current = 0;
        lastPlayedPositionRef.current = null;
      }

      scrollRef.current.style.transform = `translateX(${positionRef.current}px)`;
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, notes]);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="animated-notes-wrapper">
      <div className="animated-notes-container">
        <div className="notes-scroll" ref={scrollRef}>
          {[...notes, ...notes, ...notes].map((note, index) => (
            <img key={index} src={note.src} alt={`${note.name} Note`} className="note-image" />
          ))}
        </div>
      </div>
      <button className="play-button" onClick={togglePlay}>
        {isPlaying ? 'Pause' : 'Play'} Animation
      </button>
    </div>
  );
};

export default AnimatedNotes;
